import { useState, useEffect } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import { useAttention, type Difficulty, type AttentionMode } from './useAttention'
import { themes as themesApi, activities as activitiesApi } from '../../api/client'
import type { ThemeSummary } from '../../api/client'
import { toGameItemsFromTheme } from '../utils'
import { useTimer } from '../useTimer'
import GameSetupScreen from '../GameSetupScreen'
import GameCompleteScreen from '../GameCompleteScreen'
import { formatGameTime } from '../utils'
import SaveErrorBanner from '../SaveErrorBanner'
import GamePlayHeader from '../GamePlayHeader'
import { ss } from '../gameSetupStyles'
import { useSessionMode } from '../../professional/SessionModeContext'
import { useGameCompletion } from '../../hooks/useGameCompletion'
import { useGameSession } from '../../hooks/useGameSession'
import { useActivitySession } from '../../hooks/useActivitySession'
import { COLORS, FONT, RADIUS, SHADOWS } from '../../styles/tokens'
import type { AttentionCondition, AttentionWord, MarkType } from './AttentionData'
import { MARKS_FOR_DIFFICULTY } from './AttentionData'

// ── Types ─────────────────────────────────────────────────────────────────────

interface ThemePool {
  slug:  string
  label: string
  words: string[]
}

type GameScreen = 'setup' | 'playing' | 'complete'

interface AttentionConfig {
  difficulty:   Difficulty
  themeIds:     number[]
  themeSlugs:   string[]
  mode:         AttentionMode
}

interface AttentionGameProps {
  onBack:             () => void
  onGoToSubscription: () => void
}

// ── Mark visual helpers ───────────────────────────────────────────────────────

const MARK_ICONS: Record<MarkType, string> = {
  circle:        '○',
  underline:     '▁',
  strikethrough: '—',
}

const MARK_LABELS_ES: Record<MarkType, string> = {
  circle:        'Rodear',
  underline:     'Subrayar',
  strikethrough: 'Tachar',
}

const MARK_LABELS_EN: Record<MarkType, string> = {
  circle:        'Circle',
  underline:     'Underline',
  strikethrough: 'Cross out',
}

function markLabel(mark: MarkType, lang: string): string {
  return lang === 'en' ? MARK_LABELS_EN[mark] : MARK_LABELS_ES[mark]
}

function wordStyle(
  word:       AttentionWord,
  conditions: AttentionCondition[],
  isWrong:    boolean,
): React.CSSProperties {
  const base: React.CSSProperties = {
    display:        'inline-flex',
    alignItems:     'center',
    justifyContent: 'center',
    padding:        '10px 16px',
    margin:         6,
    borderRadius:   RADIUS.md,
    fontSize:       FONT.size.md,
    fontWeight:     700,
    cursor:         'pointer',
    userSelect:     'none',
    transition:     'all 0.15s ease',
    minHeight:      48,
    minWidth:       64,
    border:         `2px solid ${COLORS.borderWarm}`,
    background:     COLORS.bgCard,
    color:          COLORS.textDark,
    position:       'relative',
  }

  if (isWrong) {
    return { ...base, background: '#FFF0F0', border: `2px solid ${COLORS.errorText}`, animation: 'wm-shake 0.4s ease' }
  }

  if (word.mark === 'none') return base

  const condMark = word.condIdx !== null ? conditions[word.condIdx].mark : null
  const markColor = word.mark === condMark ? COLORS.greenLight : COLORS.textBody

  switch (word.mark) {
    case 'circle':
      return {
        ...base,
        border:     `2.5px solid ${markColor}`,
        borderRadius: 999,
        background:  '#F0FAF4',
        color:       COLORS.greenDark,
      }
    case 'underline':
      return {
        ...base,
        textDecoration: 'underline',
        textDecorationColor: markColor,
        textDecorationThickness: '2.5px',
        background:  '#FFFBF0',
        color:       COLORS.textDark,
      }
    case 'strikethrough':
      return {
        ...base,
        textDecoration: 'line-through',
        textDecorationColor: markColor,
        textDecorationThickness: '2.5px',
        background:  '#F5F0FF',
        color:       COLORS.textBody,
      }
  }
}

// ── Root component ────────────────────────────────────────────────────────────

export default function AttentionGame({ onBack, onGoToSubscription }: AttentionGameProps) {
  const { t, language } = useTranslation()
  const sessionMode = useSessionMode()
  const { savedSession, saveConfig, clear } = useActivitySession<AttentionConfig>('attention', !sessionMode)
  const restored = savedSession?.config ?? null

  const [screen,     setScreen]     = useState<GameScreen>(sessionMode ? 'playing' : (restored ? 'playing' : 'setup'))
  const [difficulty, setDifficulty] = useState<Difficulty>(
    (sessionMode?.config.difficulty as Difficulty) ?? restored?.difficulty ?? 'easy',
  )
  const [mode, setMode] = useState<AttentionMode>(
    (sessionMode?.config as Record<string, unknown> | undefined)?.mode as AttentionMode ?? restored?.mode ?? 'words',
  )
  const [availableThemes,  setAvailableThemes]  = useState<ThemeSummary[]>([])
  const [selectedThemeIds, setSelectedThemeIds] = useState<number[]>(
    ((sessionMode?.config as Record<string, unknown> | undefined)?.themeIds as number[] | undefined) ?? restored?.themeIds ?? [],
  )
  const [themePools,    setThemePools]    = useState<ThemePool[]>([])
  const [themeLoading,  setThemeLoading]  = useState(false)
  const [classicWords,  setClassicWords]  = useState<{ es: string[]; en: string[] } | undefined>(undefined)

  // Load classic word list overrides from activity data
  useEffect(() => {
    activitiesApi.get('attention')
      .then((detail) => {
        const d = detail.data as { esWords?: string[]; enWords?: string[] } | null
        if (d?.esWords?.length && d?.enWords?.length) {
          setClassicWords({ es: d.esWords, en: d.enWords })
        }
      })
      .catch(() => {})
  }, [])

  // Load available themes on mount
  useEffect(() => {
    themesApi.list('attention')
      .then(setAvailableThemes)
      .catch(() => {})
  }, [])

  // Load words for each selected theme
  useEffect(() => {
    if (selectedThemeIds.length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setThemePools([])
      return
    }
    setThemeLoading(true)
    Promise.all(
      selectedThemeIds.map(id => {
        const meta = availableThemes.find(t => t.id === id)
        if (!meta) return null
        return themesApi.get(meta.slug)
          .then(theme => {
            const items = toGameItemsFromTheme(theme.images, language)
            return { slug: meta.slug, label: language === 'es' ? meta.nameEs : meta.nameEn, words: items.map(i => i.word.toUpperCase()) }
          })
          .catch(() => null)
      }),
    ).then(results => {
      setThemePools(results.filter((r): r is ThemePool => r !== null))
    }).finally(() => setThemeLoading(false))
  }, [selectedThemeIds, availableThemes, language])

  const hook = useAttention(difficulty, language as 'es' | 'en', themePools, classicWords, mode)
  const { gameComplete, moves, startTime, endTime } = hook.state

  const condWords        = hook.state.words.filter(w => w.condIdx !== null)
  const condWordsCorrect = condWords.filter(w => {
    const cond = hook.state.conditions[w.condIdx!]
    return cond && w.mark === cond.mark
  })

  const { sessionId } = useGameSession({
    activitySlug: 'attention',
    difficulty,
    mode,
    enabled: screen === 'playing',
  })

  const { saveError, clearSaveError } = useGameCompletion({
    gameComplete,
    activitySlug:  'attention',
    difficulty,
    moves,
    startTime,
    endTime,
    pairsTotal:   condWords.length,
    pairsMatched: gameComplete ? condWordsCorrect.length : 0,
    sessionId,
    onCompleted:  () => { clear(); setScreen('complete') },
  })

  const handleStart = () => {
    hook.resetGame()
    saveConfig({
      difficulty,
      themeIds:   selectedThemeIds,
      themeSlugs: themePools.map(t => t.slug),
      mode,
    })
    setScreen('playing')
  }

  function toggleTheme(id: number) {
    setSelectedThemeIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    )
  }

  const lang = language === 'en' ? 'en' : 'es'

  if (sessionMode && themeLoading) {
    return <div style={s.loadRoot}>…</div>
  }

  return (
    <div style={s.root}>
      {saveError && <SaveErrorBanner message={t.common.saveError} dismissLabel={t.common.dismissError} onDismiss={clearSaveError} />}

      {screen === 'setup' && (
        <SetupScreen
          difficulty={difficulty}
          mode={mode}
          availableThemes={availableThemes}
          selectedThemeIds={selectedThemeIds}
          themeLoading={themeLoading}
          onDifficultyChange={d => setDifficulty(d as Difficulty)}
          onModeChange={m => setMode(m as AttentionMode)}
          onToggleTheme={toggleTheme}
          onStart={handleStart}
          onBack={onBack}
          onGoToSubscription={onGoToSubscription}
        />
      )}

      {screen === 'playing' && (
        <PlayScreen
          state={hook.state}
          difficulty={difficulty}
          onTapWord={hook.tapWord}
          onBack={sessionMode ? onBack : () => { clear(); setScreen('setup') }}
          lang={lang}
          mode={mode}
        />
      )}

      {screen === 'complete' && (() => {
        const at = t.attention
        const cr = hook.state.checkResult
        const accuracy = cr && cr.total > 0 ? Math.round((cr.correct / cr.total) * 100) : 100
        return (
          <GameCompleteScreen
            emoji={mode === 'numbers' ? '🔢' : '🔤'}
            title={at.complete.title}
            subtitle={at.complete.subtitle}
            stats={[
              { value: `${accuracy}%`,                          label: at.complete.accuracyLabel },
              { value: formatGameTime(hook.state.startTime, hook.state.endTime), label: at.complete.timeLabel },
              { value: at.difficulty[difficulty].label,         label: at.complete.levelLabel },
            ]}
            playAgainLabel={at.complete.playAgain}
            changeLevelLabel={at.complete.changeLevel}
            backLabel={at.backToDashboard}
            onPlayAgain={() => {
              hook.resetGame()
              saveConfig({ difficulty, themeIds: selectedThemeIds, themeSlugs: themePools.map(t => t.slug), mode })
              setScreen('playing')
            }}
            onChangeLevel={() => { clear(); setScreen('setup') }}
            onBack={onBack}
          />
        )
      })()}
    </div>
  )
}

// ── Setup Screen ──────────────────────────────────────────────────────────────

function SetupScreen({
  difficulty, mode, availableThemes, selectedThemeIds, themeLoading,
  onDifficultyChange, onModeChange, onToggleTheme, onStart, onBack, onGoToSubscription,
}: {
  difficulty:         Difficulty
  mode:               AttentionMode
  availableThemes:    ThemeSummary[]
  selectedThemeIds:   number[]
  themeLoading:       boolean
  onDifficultyChange: (d: string) => void
  onModeChange:       (m: string) => void
  onToggleTheme:      (id: number) => void
  onStart:            () => void
  onBack:             () => void
  onGoToSubscription: () => void
}) {
  const { t, language } = useTranslation()
  const at = t.attention

  const MODES = [
    { id: 'words' as const,   label: at.modeWords },
    { id: 'numbers' as const, label: at.modeNumbers },
  ]

  const unitLabel = mode === 'numbers'
    ? (language === 'en' ? 'numbers' : 'números')
    : (language === 'en' ? 'words' : 'palabras')
  const ruleWord = (n: number) =>
    language === 'en' ? `${n} rule${n > 1 ? 's' : ''}` : `${n} regla${n > 1 ? 's' : ''}`

  const DIFFICULTIES = [
    { id: 'easy',   label: at.difficulty.easy.label,   hint: `20 ${unitLabel} · ${ruleWord(1)}` },
    { id: 'medium', label: at.difficulty.medium.label, hint: `40 ${unitLabel} · ${ruleWord(2)}` },
    { id: 'hard',   label: at.difficulty.hard.label,   hint: `60 ${unitLabel} · ${ruleWord(3)}` },
  ]

  const preview = (
    <div style={s.preview}>
      <div style={s.previewCloud}>
        {['CASA', 'ÁRBOL', 'SOL', 'MARIPOSA', 'LIBRO', 'VIENTO'].map((w, i) => (
          <span key={i} style={{ ...s.previewWord, ...(i === 1 ? s.previewCircle : i === 4 ? s.previewUnderline : {}) }}>
            {w}
          </span>
        ))}
      </div>
      <p style={s.previewHint}>{at.previewHint}</p>
    </div>
  )

  return (
    <GameSetupScreen
      title={at.title}
      subtitle={mode === 'numbers' ? at.subtitleNumbers : at.subtitle}
      backLabel={at.backToDashboard}
      onBack={onBack}
      difficulties={DIFFICULTIES}
      selectedDifficulty={difficulty}
      onDifficultyChange={onDifficultyChange}
      chooseDifficultyLabel={at.chooseDifficultyTitle}
      preview={preview}
      startLabel={at.startBtn}
      onStart={onStart}
      themeLoading={themeLoading}
    >
      {/* Mode selector */}
      <section className="fade-up" style={{ animationDelay: '0.02s' }}>
        <h2 style={ss.optionLabel}>{at.chooseModeTitle}</h2>
        <div style={ss.modeGrid}>
          {MODES.map(m => {
            const isActive = mode === m.id
            return (
              <button
                key={m.id}
                onClick={() => onModeChange(m.id)}
                style={{ ...ss.modeCard, ...(isActive ? ss.modeCardActive : {}) }}
                aria-pressed={isActive}
              >
                <span style={{ fontSize: FONT.size.md, fontWeight: 700, color: COLORS.textDark }}>{m.label}</span>
              </button>
            )
          })}
        </div>
      </section>

      {/* Custom multi-select theme section — hidden in numbers mode */}
      {mode === 'words' && availableThemes.length > 0 && (
        <section className="fade-up" style={{ animationDelay: '0.05s' }}>
          <h2 style={ss.optionLabel}>{at.chooseThemesTitle}</h2>
          <p style={s.themesHint}>{at.chooseThemesHint}</p>
          <div style={ss.themeRow}>
            {availableThemes.map(theme => {
              const isActive = selectedThemeIds.includes(theme.id)
              const locked   = theme.locked
              return (
                <button
                  key={theme.id}
                  onClick={() => locked ? onGoToSubscription() : onToggleTheme(theme.id)}
                  style={{ ...ss.themeBtn, ...(isActive ? ss.themeBtnActive : {}), ...(locked ? ss.themeBtnLocked : {}) }}
                  aria-pressed={isActive}
                  title={locked ? theme.requiredTier : undefined}
                >
                  <div style={{ position: 'relative' as const, width: 48, height: 48, flexShrink: 0 }}>
                    {theme.coverUrl
                      ? <img src={theme.coverUrl} alt="" style={ss.themeCover} />
                      : <span style={ss.themeEmoji}>🖼️</span>
                    }
                    {locked && <span style={ss.lockBadge}>🔒</span>}
                    {isActive && !locked && (
                      <span style={s.checkBadge}>✓</span>
                    )}
                  </div>
                  <span style={ss.themeLabel}>{language === 'es' ? theme.nameEs : theme.nameEn}</span>
                </button>
              )
            })}
          </div>
        </section>
      )}
    </GameSetupScreen>
  )
}

// ── Play Screen ───────────────────────────────────────────────────────────────

function PlayScreen({
  state, difficulty, onTapWord, onBack, lang, mode,
}: {
  state:       ReturnType<typeof useAttention>['state']
  difficulty:  import('./AttentionData').Difficulty
  onTapWord:   (id: number) => void
  onBack:      () => void
  lang:        'es' | 'en'
  mode:        AttentionMode
}) {
  const { t } = useTranslation()
  const at    = t.attention
  const timer = useTimer(state.startTime, state.endTime)

  const availableMarks = MARKS_FOR_DIFFICULTY[difficulty]

  const markedCorrectly = state.words.filter(w => {
    if (w.condIdx === null) return w.mark === 'none'
    return w.mark === state.conditions[w.condIdx].mark
  }).length
  const progress = state.words.length > 0 ? markedCorrectly / state.words.length : 0

  function conditionLabel(cond: AttentionCondition): string {
    const conditionsMap = at.conditions as Record<string, string> | undefined
    const template = conditionsMap?.[cond.type]
    if (template) {
      return template
        .replace('{x}', String(cond.param))
        .replace('{y}', String(cond.param))
        .replace('{n}', String(cond.param))
    }
    // Fallback for word condition types
    switch (cond.type) {
      case 'length_gt':   return lang === 'es'
        ? `tengan más de ${cond.param} letras`
        : `have more than ${cond.param} letters`
      case 'starts_with': return lang === 'es'
        ? `empiezan por "${cond.param}"`
        : `start with "${cond.param}"`
      case 'ends_with':   return lang === 'es'
        ? `terminan en "${cond.param}"`
        : `end with "${cond.param}"`
      case 'category':    return lang === 'es'
        ? `pertenecen a "${cond.themeLabel ?? cond.param}"`
        : `belong to "${cond.themeLabel ?? cond.param}"`
      default:            return String(cond.param)
    }
  }

  function tapInstruction(): string {
    const parts = availableMarks.map((mark, i) => {
      const n = i + 1
      const tapWord = lang === 'es' ? `${n} toque` : `${n} tap`
      return `${tapWord}: ${markLabel(mark, lang).toLowerCase()}`
    })
    const clearTap = availableMarks.length + 1
    const clearWord = lang === 'es' ? `${clearTap} toque: quitar` : `${clearTap} tap: clear`
    return [...parts, clearWord].join('  ·  ')
  }

  const itemLabel = mode === 'numbers'
    ? (lang === 'es' ? 'números' : 'numbers')
    : (lang === 'es' ? 'palabras' : 'words')

  return (
    <div style={s.playRoot}>
      <GamePlayHeader
        title={at.title}
        stats={[
          { value: state.moves, label: at.moves },
          { value: timer,       label: at.time },
        ]}
        progress={progress * 100}
        onBack={onBack}
        backLabel={at.backToDashboard}
      />

      <div style={s.playInner}>
        {/* Tap instruction */}
        <p style={s.tapInstruction}>{tapInstruction()}</p>

        {/* Rules panel */}
        <div style={s.rulesPanel}>
          {state.conditions.map((cond, i) => {
            const markedCount = state.words.filter(w => w.mark === cond.mark).length
            return (
              <div key={i} style={s.ruleRow}>
                <span style={s.ruleIcon}>{MARK_ICONS[cond.mark]}</span>
                <span style={s.ruleText}>
                  {markLabel(cond.mark, lang)}: {conditionLabel(cond)}
                </span>
                <span style={{ ...s.ruleBadge, ...(markedCount === cond.count ? s.ruleBadgeDone : {}) }}>
                  {markedCount}/{cond.count}
                </span>
              </div>
            )
          })}
        </div>

        {/* Word cloud */}
        <div style={s.wordCloud}>
          {state.words.map(word => {
            const isWrong = state.wrongFlashIds.includes(word.id)
            return (
              <button
                key={word.id}
                onClick={() => onTapWord(word.id)}
                style={wordStyle(word, state.conditions, isWrong)}
                className={isWrong ? 'wm-shake' : undefined}
                aria-label={word.text}
              >
                {word.text}
              </button>
            )
          })}
        </div>

        {/* Instruction */}
        <p style={s.instruction}>
          {!state.startTime
            ? (lang === 'es' ? `Toca los ${itemLabel} para marcarlos según las reglas` : `Tap ${itemLabel} to mark them according to the rules`)
            : state.gameComplete
              ? (lang === 'es' ? '¡Todo correcto!' : 'All correct!')
              : (mode === 'numbers' ? at.instructionPlayNumbers : at.instructionPlay)}
        </p>
      </div>
    </div>
  )
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  root: {
    minHeight:  '100vh',
    background: COLORS.bgPage,
  },
  loadRoot: {
    minHeight:      '100vh',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    background:     COLORS.bgPage,
    fontSize:       FONT.size.xl,
  },

  // Setup extras
  themesHint: {
    fontSize:     FONT.size.sm,
    color:        COLORS.textSecondary,
    marginTop:    -8,
    marginBottom: 12,
  },
  checkBadge: {
    position:   'absolute',
    bottom:     -4,
    right:      -4,
    fontSize: '0.875rem',
    fontWeight: 700,
    color:      '#fff',
    background: COLORS.green,
    borderRadius: '50%',
    width:      18,
    height:     18,
    display:    'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  },
  preview: {
    padding:      '20px 16px',
    borderRadius: RADIUS.lg,
    background:   COLORS.bgCard,
    border:       `1.5px solid ${COLORS.borderWarm}`,
    boxShadow:    SHADOWS.subtle,
    textAlign:    'center',
  },
  previewCloud: {
    display:     'flex',
    flexWrap:    'wrap',
    gap:         8,
    justifyContent: 'center',
    marginBottom: 10,
  },
  previewWord: {
    padding:      '6px 12px',
    borderRadius: RADIUS.sm,
    background:   '#F5F0F0',
    fontSize:     FONT.size.base,
    fontWeight:   700,
    color:        COLORS.textDark,
    border:       `1.5px solid ${COLORS.borderWarm}`,
  },
  previewCircle: {
    borderRadius: 999,
    border:       `2px solid ${COLORS.green}`,
    background:   COLORS.greenSurface,
    color:        COLORS.greenDark,
  },
  previewUnderline: {
    textDecoration:          'underline',
    textDecorationColor:     COLORS.textBody,
    textDecorationThickness: '2px',
  },
  previewHint: {
    fontSize: FONT.size.sm,
    color:    COLORS.textSecondary,
    margin:   0,
  },

  tapInstruction: {
    fontSize:     FONT.size.sm,
    color:        COLORS.textSecondary,
    textAlign:    'center' as const,
    marginBottom: 12,
    marginTop:    4,
  },

  // Play screen
  playRoot: {
    minHeight:  '100vh',
    background: COLORS.bgPage,
    display:    'flex',
    flexDirection: 'column',
  },
  playInner: {
    flex:    1,
    padding: '20px 16px 48px',
    maxWidth: 820,
    margin:  '0 auto',
    width:   '100%',
  },

  // Rules panel
  rulesPanel: {
    background:   COLORS.bgCard,
    borderRadius: RADIUS.card,
    border:       `1.5px solid ${COLORS.borderWarm}`,
    boxShadow:    SHADOWS.subtle,
    padding:      '16px 20px',
    marginBottom: 24,
    display:      'flex',
    flexDirection: 'column',
    gap:          10,
  },
  ruleRow: {
    display:    'flex',
    alignItems: 'center',
    gap:        10,
  },
  ruleIcon: {
    fontSize:   FONT.size.lg,
    minWidth:   28,
    textAlign:  'center',
    fontWeight: 700,
    color:      COLORS.greenDark,
  },
  ruleText: {
    flex:       1,
    fontSize:   FONT.size.md,
    color:      COLORS.textBody,
    fontWeight: 500,
  },
  ruleBadge: {
    background:   COLORS.greenSurface,
    border:       `1.5px solid ${COLORS.greenPale}`,
    borderRadius: RADIUS.sm,
    padding:      '2px 10px',
    fontSize:     FONT.size.base,
    fontWeight:   700,
    color:        COLORS.green,
    minWidth:     44,
    textAlign:    'center',
    fontVariantNumeric: 'tabular-nums',
  },
  ruleBadgeDone: {
    background: COLORS.green,
    border:     `1.5px solid ${COLORS.greenDark}`,
    color:      '#fff',
  },

  // Word cloud
  wordCloud: {
    display:     'flex',
    flexWrap:    'wrap',
    gap:         4,
    marginBottom: 24,
    padding:     '4px 0',
  },

  // Instruction
  instruction: {
    fontSize:     FONT.size.base,
    color:        COLORS.textSecondary,
    fontStyle:    'italic',
    marginBottom: 16,
    textAlign:    'center',
  },
}
